import type { Result } from '@zxing/library'

export function qrOuterOverlayFromZXingResult(result: Result) {
	const pts = result.getResultPoints().map((p) => ({
		x: p.getX(),
		y: p.getY(),
		m: (p as any).estimatedModuleSize || 0,
	}))

	if (pts.length < 3) throw new Error('Need at least 3 resultPoints')

	// --- identify alignment pattern ---
	let alignIndex = -1
	if (pts.length >= 4) {
		alignIndex = pts.findIndex((p, i) => {
			const tri = pts.filter((_, j) => j !== i)
			return pointInTriangle(p, tri[0], tri[1], tri[2])
		})

		if (alignIndex === -1) {
			alignIndex = pts
				.map((p, i) => ({
					i,
					score: pts.reduce((s, q, j) => (i === j ? s : s + dist(p, q)), 0),
				}))
				.sort((a, b) => a.score - b.score)[0].i
		}
	}

	const align = alignIndex >= 0 ? pts[alignIndex] : null
	const finder = align ? pts.filter((_, i) => i !== alignIndex) : pts

	// --- label finder centers: TL, TR, BL ---
	const [tl, tr, bl] = labelFinderCenters(finder)

	// --- estimate QR dimension ---
	const moduleSize = pts.reduce((s, p) => s + p.m, 0) / pts.length
	const modulesBetweenFinderCenters = Math.round(
		(dist(tl, tr) + dist(tl, bl)) / 2 / moduleSize,
	)
	const dimension = nearestQrDimension(modulesBetweenFinderCenters + 7)

	// --- reconstruct BR center-space corner ---
	let brCenter
	if (align) {
		const c = 1 - 3 / (dimension - 7)
		brCenter = {
			x: tl.x + (align.x - tl.x) / c,
			y: tl.y + (align.y - tl.y) / c,
		}
	} else {
		brCenter = {
			x: tr.x + bl.x - tl.x,
			y: tr.y + bl.y - tl.y,
		}
	}

	// --- build module-direction axes ---
	const ux = normalize({ x: tr.x - tl.x, y: tr.y - tl.y }) // left->right
	const uy = normalize({ x: bl.x - tl.x, y: bl.y - tl.y }) // top->bottom

	// --- expand from finder centers to outer QR border ---
	// each finder center is 3.5 modules from the outer border
	const e = 3.5 * moduleSize

	const topLeft = {
		x: tl.x - e * ux.x - e * uy.x,
		y: tl.y - e * ux.y - e * uy.y,
	}

	const topRight = {
		x: tr.x + e * ux.x - e * uy.x,
		y: tr.y + e * ux.y - e * uy.y,
	}

	const bottomLeft = {
		x: bl.x - e * ux.x + e * uy.x,
		y: bl.y - e * ux.y + e * uy.y,
	}

	const bottomRight = {
		x: brCenter.x + e * ux.x + e * uy.x,
		y: brCenter.y + e * ux.y + e * uy.y,
	}

	return [topLeft, topRight, bottomRight, bottomLeft]
}

// ---------- helpers ----------

function dist(a, b) {
	return Math.hypot(a.x - b.x, a.y - b.y)
}

function normalize(v) {
	const d = Math.hypot(v.x, v.y)
	return { x: v.x / d, y: v.y / d }
}

function nearestQrDimension(d) {
	const version = Math.max(1, Math.min(40, Math.round((d - 17) / 4)))
	return 17 + 4 * version
}

function pointInTriangle(p, a, b, c) {
	const s1 = sign(p, a, b)
	const s2 = sign(p, b, c)
	const s3 = sign(p, c, a)
	const hasNeg = s1 < 0 || s2 < 0 || s3 < 0
	const hasPos = s1 > 0 || s2 > 0 || s3 > 0
	return !(hasNeg && hasPos)
}

function sign(p1, p2, p3) {
	return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y)
}

function labelFinderCenters(finder) {
	// longest distance is TR <-> BL, remaining point is TL
	const [a, b, c] = finder
	const dab = dist(a, b),
		dac = dist(a, c),
		dbc = dist(b, c)

	let tl, tr, bl, p, q
	if (dab >= dac && dab >= dbc) {
		tl = c
		p = a
		q = b
	} else if (dac >= dab && dac >= dbc) {
		tl = b
		p = a
		q = c
	} else {
		tl = a
		p = b
		q = c
	}

	const cross = (p.x - tl.x) * (q.y - tl.y) - (p.y - tl.y) * (q.x - tl.x)

	if (cross < 0) {
		tr = p
		bl = q
	} else {
		tr = q
		bl = p
	}

	return [tl, tr, bl]
}
